import React from "react";
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  IndianRupee,
  Wallet,
  FileDown,
} from "lucide-react";

const DriverAnalyticsTopSection = ({
  username,
  driverId,

  fromDate,
  toDate,
  setFromDate,
  setToDate,

  handleApply,
  handleExport,
  navigate,

  loading,
  analytics,
  showValue,

  activeTab,
  setActiveTab,
    weeklyPoints = [],
  weeklyPointsLoading = false,
}) => {
  const cards = [
    {
      label: "Assigned Orders",
      value: analytics?.assigned_orders,
      icon: <ClipboardList className="h-6 w-6 text-blue-600" />,
      border: "border-l-blue-500",
      bg: "bg-blue-50",
      hover: "hover:bg-blue-100",
    },
    {
      label: "Successful Orders",
      value: analytics?.current_successful_orders,
      icon: <CheckCircle className="h-6 w-6 text-green-600" />,
      border: "border-l-green-500",
      bg: "bg-green-50",
      hover: "hover:bg-green-100",
    },
    {
      label: "Declined Orders",
      value: analytics?.declined_orders,
      icon: <XCircle className="h-6 w-6 text-red-600" />,
      border: "border-l-red-500",
      bg: "bg-red-50",
      hover: "hover:bg-red-100",
    },
    {
      label: "Per Day Earning",
      value: showValue(analytics?.per_day, true),
      icon: <IndianRupee className="h-6 w-6 text-indigo-600" />,
      border: "border-l-indigo-500",
      bg: "bg-indigo-50",
      hover: "hover:bg-indigo-100",
    },
    {
      label: "Per Order Earning",
      value: showValue(analytics?.per_order, true),
      icon: <Wallet className="h-6 w-6 text-emerald-600" />,
      border: "border-l-emerald-500",
      bg: "bg-emerald-50",
      hover: "hover:bg-emerald-100",
    },
  ];

  const tabs = [
    {
      key: "analytics",
      label: "Analytics",
    },
    {
      key: "reports",
      label: "Reports",
    },
    {
      key: "points",
      label: "Points",
    },
  ];

  return (
    <>
      <div className="bg-white border-b shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">
              Packky Driver Analytics — {decodeURIComponent(username || "")}
              <span className="text-gray-400"> ({driverId})</span>
            </h2>

            <p className="text-sm text-gray-500">
              Filter, analyze, process & export
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border p-2 rounded text-sm"
            />

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border p-2 rounded text-sm"
            />

            <button
              onClick={handleApply}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Apply
            </button>

            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2"
            >
              <FileDown size={16} />
              Export
            </button>

            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border rounded"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* ANALYTICS CARDS */}
        {loading && <p>Loading analytics...</p>}

        {!loading && analytics && (
          <div className="grid gap-3 md:grid-cols-5 px-4 pb-4">
            {cards.map((card, index) => (
              <div
                key={index}
                className={`group rounded-2xl border-l-4 ${card.border} ${card.bg} ${card.hover} p-4 transition-all duration-300`}
              >
                <div className="flex items-center gap-3">
                  {card.icon}

                  <div>
                    <div className="text-xs text-gray-600">{card.label}</div>
                    <div className="text-lg font-semibold text-gray-800">
                      {card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TABS */}
        <div className="bg-white border-b border-gray-200 px-4">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-1 border-b-2 text-sm font-medium ${
                  activeTab === tab.key
                    ? "border-orange-600 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* POINTS TABLE */}
      {activeTab === "points" && (
        <div className="flex-1 min-h-0 mt-5 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                Driver Weekly Performance Points
              </h3>
              <p className="text-xs text-gray-500">
                Showing data from {fromDate || "-"} to {toDate || "-"}
              </p>
            </div>

            <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600">
              {driverId}
            </span>
          </div>

          {weeklyPointsLoading ? (
            <div className="p-5 text-sm text-gray-500">Loading points...</div>
          ) : weeklyPoints?.length > 0 ? (
            <div className="h-[calc(100vh-360px)] overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="sticky top-0 z-20 bg-gray-50 text-xs uppercase text-gray-500 shadow-sm">
                  <tr>
                    <th className="px-4 py-3">S.No</th>
                    <th className="px-4 py-3">Performance ID</th>
                    <th className="px-4 py-3">Total Points</th>
                    <th className="px-4 py-3">Daily Points</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {weeklyPoints.map((item, index) => {
                    const dailyPoints = Number(item?.dailyPoints || 0);

                    return (
                      <tr key={item?.pk_performmance_id || index}>
                        <td className="px-4 py-3 text-gray-600">{index + 1}</td>

                        <td className="px-4 py-3 font-medium text-gray-800">
                          {item?.pk_performmance_id || "-"}
                        </td>

                        <td className="px-4 py-3 font-semibold text-gray-800">
                          {item?.totalPoints ?? 0}
                        </td>

                        <td
                          className={`px-4 py-3 font-semibold ${
                            dailyPoints >= 0 ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {dailyPoints > 0 ? `+${dailyPoints}` : dailyPoints}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              dailyPoints >= 0
                                ? "bg-green-50 text-green-700"
                                : "bg-red-50 text-red-700"
                            }`}
                          >
                            {dailyPoints >= 0 ? "Improved" : "Deducted"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-5 text-sm text-gray-500">
              No points data found for selected date filter.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DriverAnalyticsTopSection;